import { GenericMessageDialog, NetworkErrorDialog } from 'uicomponents';

import { SYSTEM_PERMISSION_DENIED_ERROR, USER_PERMISSION_DENIED_ERROR } from './constants';
import { MediaRoomGetters } from './getters';
import { MediaRoomMutations } from './mutations';
import { MediaRoomState } from './types';
import { isStageAccessibleToAttendee } from './utils';

import type {
  ConnectionDisconnectedReason,
  ConnectionState,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalTrack,
  IMicrophoneAudioTrack,
  ITrack,
  NetworkQuality,
  SDK_MODE,
  AIDenoiserProcessorLevel
} from '@/services/agora.types';

import analytics from '@/analytics';
import { appMode } from '@/assets/utilfunctions';
import AudioVideoSettingsModal from '@/components/AudioVideoSettingsModal.vue';
import { AGORA, LOCAL_STORAGE_KEYS } from '@/constants';
import { getAgoraRTC } from '@/services/agoraLoader';
import { getOS } from '@/services/browser.service';
import DialogService from '@/services/dialogs/DialogService';
import NotificationService from '@/services/dialogs/notifications';
import {
  getAgoraBulkToken,
  updateBroadcastRecordingLayout,
  updateDiscussionRoomRecordingLayout
} from '@/services/event.service';
import {
  closeJoiningDelayNetworkErrorModal,
  enableAgoraLogs,
  exitMediaRoom,
  getUserSuffixForAgoraToken,
  joinMediaRoom,
  MediaRoom,
  MediaUserRole,
  publishLocalTracks,
  setRole,
  shareScreen,
  unpublishLocalTracks
} from '@/services/liveMedia.service';
import { getValueFromLocalstore, saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { ActivityType, handleSpeakerActivity } from '@/services/speakerActivity.service';
import { currentUserHasStageRequest, updateOnStageRequest } from '@/services/stageControl.service';
import {
  closeTracks,
  createLocalAudioTrack,
  createLocalScreenShareTracks,
  createLocalVideoTrack,
  getCamaraDevices,
  getMicrophoneDevices,
  getSpeakerDevices,
  LocalTrack,
  LocalTrackStatus,
  setBackgroundBlurring,
  setBackgroundImage,
  setMediaDevice,
  subscribeDeviceEvents,
  switchVideoQuality,
  toggleAudioLevelTracking,
  unsetBackgroundFilter,
  toggleNoiseCancellation,
  setNoiseCancellationLevel,
  initializeNoiseCancellation,
  cleanupNoiseCancellation,
  detachNoiseCancellation,
  getEncodingBasedOnWidth,
  createLocalVideoTrackWithCustomQuality
} from '@/services/stream.service';
import { getVideoQNARequestForCurrentUserByStatus } from '@/services/videoQNA.service';
import store, { RootState } from '@/store';
import { scheduleUserFetch } from '@/store/schedulers';
import { NETWORK_STRINGS } from '@/utils/commonStrings';
import { getNetworkQualityDatapoints } from '@/utils/networkQuality';
import { getAggregate } from '@/utils/stats';
import { asyncRetry } from '@/utils/utils';

export type MediaRoomContext = {
  state: MediaRoomState;
  rootState: RootState;
  getters: MediaRoomGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof MediaRoomMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
};

const actions = {
  async fetchBulkAgoraToken(context: MediaRoomContext) {
    try {
      const broadcasts = context.rootState.event.event.agenda.broadcasts;
      if (!broadcasts.length) {
        return [];
      }
      const tokenPayload: {
        suffix: string;
        channel_suffix: string;
        room: any;
      }[] = [];
      broadcasts.forEach((element: any) => {
        const userSuffix = getUserSuffixForAgoraToken();
        const payload = {
          suffix: userSuffix,
          channel_suffix: '',
          room: element.id
        };
        tokenPayload.push(payload);
      });
      const tokens = await getAgoraBulkToken(tokenPayload);
      context.commit('setBulkAgoraTokens', tokens.data);
      return tokens.data;
    } catch (error) {
      loggerService.error('error getting token from bulk agora tokens', error);
      return [];
    }
  },

  async joinRoom(
    context: MediaRoomContext,
    payload: {
      channelName: string;
      role: MediaUserRole;
      mode: SDK_MODE;
      forceFetchFromApi?: boolean;
    }
  ) {
    if (!payload.channelName) {
      loggerService.error('Invalid JoinRoom Request, no channelName', payload);
      return;
    }
    const existingRoomIfAny = context.state.mediaRooms[payload.channelName] || {};
    if (existingRoomIfAny.desiredStatus === 'CONNECTED' && existingRoomIfAny.client) {
      return;
    }
    context.commit('setMediaChannelDesiredStatus', {
      channelName: payload.channelName,
      desiredStatus: 'CONNECTED'
    });
    try {
      const room = await joinMediaRoom(payload.channelName, payload.role, payload.mode, payload.forceFetchFromApi);
      context.commit('addMediaRoom', room);
    } catch (error) {
      loggerService.error('Failed to join media room, keeping desired state for potential retry', {
        channelName: payload.channelName,
        error
      });
      // Keep desiredStatus as 'CONNECTED' so the system can retry or user can see connection is still desired
      // Only the actual status will reflect the failed state
      NotificationService.toast(
        'Connection Error',
        'Failed to connect to the media room. Please refresh the page and try again.',
        'error'
      );
      throw error;
    }
  },
  async changeUserRoleInRoom(
    context: MediaRoomContext,
    {
      channelName,
      userRole
    }: {
      channelName: MediaRoom['channelName'];
      userRole: MediaRoom['userRole'];
    }
  ) {
    const mediaRoom = context.state.mediaRooms[channelName];

    if (!mediaRoom) {
      return;
    }

    //If made an attendee, all tracks must be unpublished
    if (userRole === AGORA.ROLE.ATTENDEE) {
      await context.dispatch('unpublishLocalTracks', {
        channelName,
        mediaTrackType: 'audio',
        forceDestroyTrack: true
      });
      await context.dispatch('unpublishLocalTracks', {
        channelName,
        mediaTrackType: 'video',
        forceDestroyTrack: true
      });
    }

    // Checking if there is an actual user role change and proceeding only then
    // Above code for unpublishing irrespective, exists to make sure unwanted tracks are removed
    if (mediaRoom?.userRole === userRole) {
      return;
    }

    await setRole(mediaRoom.client, channelName, userRole);
    context.commit('setUserRoleInMediaRoom', { channelName, userRole });

    if (userRole === AGORA.ROLE.SPEAKER) {
      if (currentUserHasStageRequest(channelName)) {
        updateOnStageRequest(context.rootState.user.currentUser?.id || '', 'COMPLETED');
      }

      context.dispatch('createLocalAudioVideoTracks', {
        channelName
      });
    }
  },
  async createLocalTrack(
    context: MediaRoomContext,
    {
      channelName,
      mediaTrackType,
      refreshOnFailure = false,
      shouldMute
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
      refreshOnFailure: boolean;
      shouldMute?: boolean;
    }
  ) {
    const isLocalRecording = context.rootState.event.event.is_local_recording;
    const deviceType = mediaTrackType == 'video' ? 'camera' : 'microphone';

    const hasExistingTrack = context.state.localTracks.find(
      track => track.mediaTrackType === mediaTrackType && track.channelName === channelName
    );

    if (hasExistingTrack) {
      toggleAudioLevelTracking('on');
      context.dispatch('checkAndPublishToChannel', { channelName });
      if (isLocalRecording && shouldMute !== undefined) {
        hasExistingTrack.track?.setMuted(shouldMute);
        if (shouldMute) {
          context.state.mediaRooms[channelName].client.unpublish([hasExistingTrack.track as ILocalTrack]);
        }
      }

      return;
    }

    const trackObj: LocalTrack = {
      channelName,
      mediaTrackType,
      trackStatus: LocalTrackStatus.CREATING
    };
    context.commit('setLocalTrack', trackObj);

    let track: any = null;
    let dialogCloser: any = () => ({});
    const timeoutHandler = setTimeout(() => {
      const { close } = NotificationService.toast(
        `It's taking too long to get your ${mediaTrackType === 'audio' ? 'microphone' : 'camera'} stream`,
        'Please restart your browser or device if this issue persists.'
      );
      dialogCloser = close;
    }, 10000);

    context.commit('setRtcDeviceAccessError', {
      errorDevice: deviceType,
      errorFlag: false
    });

    context.commit('setRtcDeviceBrowserPermission', {
      deviceType: deviceType,
      permission: true
    });

    context.commit('setRtcDeviceSystemPermission', {
      deviceType: deviceType,
      permission: true
    });

    let localDuplicateTrack: ICameraVideoTrack | undefined = undefined;

    try {
      if (mediaTrackType == 'audio') {
        const deviceId = context.state.currentDevices.microphone?.deviceId;
        track = await createLocalAudioTrack({ microphoneId: deviceId });
        await context.dispatch('enableNoiseCancellationAndWaitForUpdate', track);
      }
      if (mediaTrackType == 'video') {
        const deviceId = context.state.currentDevices.camera?.deviceId;
        if (
          context.rootState.event.event.is_local_recording &&
          !channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE) &&
          !channelName.includes(AGORA.TEST_CHANNEL)
        ) {
          // handle flow with local duplicate track for local recordings
          // sometimes, we get cached track from local video track instead of fresh one
          // to handle that, for local recordings, we create 1080p track and then
          // after creating duplicate track, we set encoder to 720p
          track = (await createLocalVideoTrack(
            {
              cameraId: deviceId,
              encoderConfig: {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                frameRate: { ideal: 30 }
              }
            },
            channelName
          )) as ICameraVideoTrack;
          localDuplicateTrack = await context.dispatch('createLocalDuplicateTrack');
          await track.setEncoderConfiguration('720p_1');
        } else {
          track = (await createLocalVideoTrack(
            {
              cameraId: deviceId
            },
            channelName
          )) as ICameraVideoTrack;
        }
      }

      clearTimeout(timeoutHandler);
      dialogCloser();

      context.dispatch('initializeDevices');
      if (track) {
        context.commit('updateLocalTrack', {
          channelName,
          mediaTrackType,
          updateObj: {
            trackId: track.getTrackId(),
            track: track,
            trackStatus: LocalTrackStatus.CREATED,
            localDuplicateTrack,
            isDuplicateTrackLive: false
          }
        });

        if (mediaTrackType === 'video') {
          await context.dispatch('enableVideoFilterAndWaitForUpdate', {
            track,
            hasFilters: !!context.state.currentBackgroundFilter
          });
        }
      }
      toggleAudioLevelTracking('on');
      subscribeDeviceEvents();
      context.dispatch('checkAndPublishToChannel', { channelName, kind: mediaTrackType });
    } catch (error) {
      clearTimeout(timeoutHandler);
      loggerService.warn(`Error creating ${mediaTrackType} stream`, JSON.stringify(error));

      context.commit('setStageControl', {
        [mediaTrackType]: false
      });

      if ((error as any).code === 'NOT_READABLE') {
        context.commit('setRtcDeviceAccessError', {
          errorDevice: deviceType,
          errorFlag: true
        });
      } else if ((error as any).code === 'PERMISSION_DENIED') {
        if (
          (error as any).message === 'AgoraRTCError PERMISSION_DENIED: NotAllowedError: Permission denied by system'
        ) {
          context.commit('setRtcDeviceSystemPermission', {
            deviceType: deviceType,
            permission: false
          });
        } else {
          context.commit('setRtcDeviceBrowserPermission', {
            deviceType: deviceType,
            permission: false
          });
        }
      } else if ((error as any).code === 'DEVICE_NOT_FOUND') {
        context.commit('setRtcDeviceSystemPermission', {
          deviceType: deviceType,
          permission: false
        });
      } else {
        context.commit('setRtcDeviceAccessError', {
          errorDevice: deviceType,
          errorFlag: true
        });
      }

      context.dispatch('openSettingsDialog', { page: 'broadcasts' });
      context.commit('removeLocalTrack', {
        channelName,
        mediaTrackType
      });

      if (refreshOnFailure) {
        window.location.reload();
      }
    }
  },
  async createLocalAudioVideoTracks(context: MediaRoomContext, payload: { channelName: MediaRoom['channelName'] }) {
    const { channelName } = payload;
    const isLocalRecording = context.rootState.event.event.is_local_recording;
    const isRecordingLocalMainStage = isLocalRecording && !channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);
    if (context.state.stageControls.audio || isRecordingLocalMainStage) {
      context.dispatch('createLocalTrack', {
        channelName,
        mediaTrackType: 'audio',
        shouldMute: isLocalRecording ? !context.state.stageControls.audio : undefined
      });
    }
    if (context.state.stageControls.video || isRecordingLocalMainStage) {
      context.dispatch('createLocalTrack', {
        channelName,
        mediaTrackType: 'video',
        shouldMute: isLocalRecording ? !context.state.stageControls.video : undefined
      });
    }
  },
  async shareScreen(context: MediaRoomContext, payload: { channelName: MediaRoom['channelName']; slideUrl?: string }) {
    const { channelName } = payload;
    const mediaRoom = context.state.mediaRooms[channelName];
    if (!mediaRoom) {
      loggerService.warn('Screen share media room not found');
      return;
    }
    context
      .dispatch('createLocalScreenShareTracks', payload)
      .then(success => {
        if (success) {
          shareScreen(mediaRoom);
          handleSpeakerActivity(() => Promise.resolve(), ActivityType.ScreenShareStart, ActivityType.ScreenShareFailed);
        }
      })
      .catch(() => {
        loggerService.error('Error creating local screen share tracks');
        handleSpeakerActivity(() => Promise.reject(), ActivityType.ScreenShareStart, ActivityType.ScreenShareFailed);
      });
  },
  updateRecordingScreenShareLayout(context: MediaRoomContext, screenShareUID: string | null) {
    try {
      if (context.rootState.route?.params?.id) {
        const isDiscussionRoomPage = context.rootState.route?.fullPath?.includes('/discussion-room');
        const isBroadcastRoomPage = context.rootState.route?.fullPath?.includes('/broadcast');
        const currentRouteID = context.rootState.route.params.id;
        const currentEvent = context.rootGetters['event/currentEvent'];
        if (currentEvent) {
          if (isBroadcastRoomPage) {
            return updateBroadcastRecordingLayout(currentRouteID, screenShareUID);
          }
          if (isDiscussionRoomPage) {
            return updateDiscussionRoomRecordingLayout(currentRouteID, screenShareUID);
          }
        }
      }
    } catch (err) {
      loggerService.error('Error adding screen share to recording!', err);
    }
  },
  triggerLayoutUpdate(context: MediaRoomContext, user: IAgoraRTCRemoteUser) {
    try {
      if (!user) {
        return;
      }

      const currentUserId = context.rootState.user.currentUser?.id;

      if (!currentUserId) {
        return;
      }
      const streamUID = user.uid.toString();

      const isCurrentUserScreenSharing =
        streamUID.includes(currentUserId) && streamUID.includes(AGORA.UID_SUFFIX.SCREEN_STREAM);

      if (!isCurrentUserScreenSharing) {
        return;
      }

      return context.dispatch('updateRecordingScreenShareLayout', (user as any)?._uintid || '');
    } catch (err) {
      loggerService.error('Error while triggering the layout change or recording!', err);
    }
  },
  async createLocalScreenShareTracks(
    context: MediaRoomContext,
    payload: { channelName: MediaRoom['channelName']; slideUrl?: string }
  ) {
    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };
    const { channelName, slideUrl } = payload;
    try {
      context.commit('setScreenTrack', {
        channelName,
        mediaTrackType: 'video',
        trackStatus: LocalTrackStatus.CREATING
      });

      let tracks: ILocalTrack | ILocalTrack[] = [];
      if (slideUrl) {
        tracks = await context.dispatch('sidebar/startSlidesCanvas', {}, { root: true });
      } else {
        tracks = await createLocalScreenShareTracks(channelName);
      }
      const videoTrack = tracks[0];
      const audioTrack = tracks[1];

      context.commit('updateScreenTrack', {
        channelName,
        mediaTrackType: 'video',
        updateObj: {
          trackId: videoTrack.getTrackId(),
          track: videoTrack,
          trackStatus: LocalTrackStatus.CREATED
        }
      });

      if (audioTrack) {
        context.commit('setScreenTrack', {
          channelName,
          mediaTrackType: 'audio',
          trackId: audioTrack.getTrackId(),
          track: audioTrack,
          trackStatus: LocalTrackStatus.CREATED
        });
      }
      return true;
    } catch (error) {
      if ((error as any).message === SYSTEM_PERMISSION_DENIED_ERROR && getOS()?.os === 'Mac OS') {
        DialogService.show(
          GenericMessageDialog,
          {
            title: i18n.global.t('@@translate@@ Screen Share'),
            message: i18n.global.t(
              '@@translate@@ Browser might not have screen-recording permission on your computer.'
            ),

            resolveLabel: i18n.global.t('@@translate@@ Open System Preferences')
          },
          {
            cancelOnClickOutside: false,
            on: {
              onHide: async () => {
                window.open('x-apple.systempreferences:com.apple.preference.security?Privacy_ScreenCapture', '_blank');
              }
            }
          }
        );
      } else if ((error as any).message === USER_PERMISSION_DENIED_ERROR) {
        loggerService.warn('User cancelled the screen share!!');
      } else {
        NotificationService.toast(
          `${i18n.global.t('@@translate@@ Error creating Screen stream')}`,
          `${i18n.global.t(
            '@@translate@@ Permission to share screen is not granted. Please *click here* to go through troubleshooting docs.'
          )}`,
          'error',
          null,
          AGORA.NOTIFICATION_TIMEOUT,
          [
            {
              type: 'link',
              value: 'https://help.goldcast.io/hc/en-us/articles/4404983187995-Screensharing-How-When-To-Use-It-'
            }
          ]
        );
      }
      context.commit('removeScreenTracks');
      context.dispatch('sidebar/stopLocalSlideRecording', {}, { root: true });

      return false;
    }
  },
  async checkAndPublishToChannel(
    context: MediaRoomContext,
    { channelName, kind }: { channelName: MediaRoom['channelName']; kind?: 'audio' | 'video' }
  ) {
    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };
    // Shouldn't publish streams to backstage in Live Streaming Mode (Hybrid Events)
    if (context.rootState.event.stage?.isLiveStreamMode && channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
      return;
    }

    const mediaRoom = context.state.mediaRooms[channelName];

    if (!mediaRoom) {
      return;
    }

    if (mediaRoom?.mode === AGORA.MODE.LIVE && mediaRoom?.userRole === AGORA.ROLE.ATTENDEE) {
      return;
    }

    /**
     * List of all tracks belonging to channelName that are ready to be published
     */
    let localTracks: LocalTrack[] = context.state.localTracks.filter(
      (track: LocalTrack) =>
        track.channelName === channelName &&
        track.track &&
        [LocalTrackStatus.CREATED, LocalTrackStatus.UNPUBLISHED].includes(track.trackStatus)
    );

    const isLocalRecording = context.rootState.event.event.is_local_recording;

    if (kind && isLocalRecording) {
      localTracks = localTracks.filter((track: LocalTrack) => track.mediaTrackType === kind);
    }

    const isRecordingLocalMainStage = isLocalRecording && !channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);

    if (!context.state.stageControls?.audio && !isRecordingLocalMainStage) {
      localTracks = localTracks.filter((track: LocalTrack) => track.mediaTrackType !== 'audio');
    }

    if (!localTracks.length) {
      // Nothing to publish
      return;
    }

    const handlePublishingError = (error: Error) => {
      if (context.state.mediaRooms[channelName]?.desiredStatus === 'CONNECTED') {
        NotificationService.toast(
          `${i18n.global.t('@@translate@@ Error publishing stream')}`,
          `${i18n.global.t('@@translate@@ Could not publish stream. Please refresh and try again!')}`,
          'error',
          null,
          AGORA.NOTIFICATION_TIMEOUT
        );
        loggerService.captureException(<Error>error);
        updateTrackStatusForChannel(LocalTrackStatus.CREATED);
      }
    };

    // TODO: Should we be doing this in a loop?
    const updateTrackStatusForChannel = (status: LocalTrackStatus) => {
      localTracks.forEach((t: LocalTrack) => {
        context.commit('updateLocalTrack', {
          channelName,
          mediaTrackType: t.mediaTrackType,
          updateObj: {
            trackStatus: status
          }
        });
      });
    };

    if (mediaRoom?.status === 'CONNECTED') {
      updateTrackStatusForChannel(LocalTrackStatus.PUBLISHING);
      const trackObjects = <ILocalTrack[]>localTracks.map(t => t.track);
      try {
        for (const track of trackObjects) {
          const isMuted = !context.state.stageControls[track.trackMediaType];

          await asyncRetry(
            async () => {
              track.setMuted(isMuted);

              if (!isMuted) {
                await publishLocalTracks(mediaRoom.client, [track]);
              }
            },
            {
              retries: 5,
              delayMs: 500,
              onRetry: (e, attempt) => loggerService.warn(`Retry attempt ${attempt} for track ${track.getTrackId()}`),
              onMaxReached: e => loggerService.warn(`Max retries reached for track ${track.getTrackId()}`)
            }
          );
        }
        updateTrackStatusForChannel(LocalTrackStatus.PUBLISHED);
        context.dispatch('subscribeToTrackEnded', { localTracks, shouldUnsubscribe: false });
      } catch (error) {
        handlePublishingError(error as Error);
      }
    }
  },
  async checkAndPublishScreenToChannel(
    context: MediaRoomContext,
    { channelName }: { channelName: MediaRoom['channelName'] }
  ) {
    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };
    /**
     * List of all tracks belonging to channelName that are ready to be published
     */
    const tracks: LocalTrack[] = context.state.screenTracks.filter(
      (track: LocalTrack) =>
        track.channelName === channelName && track.track && track.trackStatus === LocalTrackStatus.CREATED
    );

    if (!tracks.length) {
      // Nothing to publish
      return;
    }

    const mediaRoom = context.state.mediaRooms[channelName];
    const remoteStreamsKeys = Object.keys(mediaRoom?.remoteStreams || {});
    const hasRemoteScreenStream = remoteStreamsKeys?.some((uid: string) =>
      uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM)
    );

    // race condition
    if (hasRemoteScreenStream) {
      NotificationService.toast(
        `${i18n.global.t('@@translate@@ Error publishing screen')}`,
        `${i18n.global.t('@@translate@@ Error publishing screen because someone else started screen share already.')}`,
        'error',
        null,
        AGORA.NOTIFICATION_TIMEOUT
      );
      context.dispatch('stopScreenSharing', { channelName });
      return;
    }

    // TODO: Should we be doing this in a loop?
    const updateTrackStatusForChannel = (status: LocalTrackStatus) => {
      tracks.forEach((t: LocalTrack) => {
        context.commit('updateScreenTrack', {
          channelName,
          mediaTrackType: t.mediaTrackType,
          updateObj: {
            trackStatus: status
          }
        });
      });
    };

    if (mediaRoom.screenStatus === 'CONNECTED') {
      updateTrackStatusForChannel(LocalTrackStatus.PUBLISHING);
      const trackObjects = <ILocalTrack[]>tracks.map(t => t.track);
      try {
        await publishLocalTracks(mediaRoom.screenShareClient, trackObjects);
        updateTrackStatusForChannel(LocalTrackStatus.PUBLISHED);
      } catch (error) {
        NotificationService.toast(
          `${i18n.global.t('@@translate@@ Error publishing screen')}`,
          `${i18n.global.t('@@translate@@ Error publishing screen. Please refresh and try again!')}`,
          'error',
          null,
          AGORA.NOTIFICATION_TIMEOUT
        );
        loggerService.captureException(<Error>error);
        updateTrackStatusForChannel(LocalTrackStatus.CREATED);
      }
    }
  },
  async closeTrackAndReleaseResources(
    context: MediaRoomContext,
    {
      channelName,
      mediaTrackType,
      forceDestroyTrack = false
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
      forceDestroyTrack: Boolean;
    }
  ) {
    // we can have multiple local tracks for audio tracks - so while unpublishing audio track
    // we need to pass and array of local tracks
    const localTracks = context.state.localTracks.filter(
      el =>
        el.channelName === channelName &&
        el.mediaTrackType == mediaTrackType &&
        (el.trackStatus == LocalTrackStatus.UNPUBLISHED || el.trackStatus == LocalTrackStatus.CREATED)
    );

    const isStreamRequired =
      !forceDestroyTrack &&
      mediaTrackType === 'audio' &&
      context.state.mediaRooms?.[channelName]?.desiredStatus === 'CONNECTED' &&
      context.state.mediaRooms?.[channelName]?.userRole === AGORA.ROLE.SPEAKER;

    if (isStreamRequired) {
      return;
    }

    await closeTracks(<ILocalTrack[]>localTracks.map(t => t?.track));
    await closeTracks(<ILocalTrack[]>localTracks.map(t => t?.localDuplicateTrack));
    context.commit('removeLocalTrack', {
      channelName: channelName,
      mediaTrackType: mediaTrackType
    });
  },
  async muteLocalTracks(
    context: MediaRoomContext,
    {
      channelName,
      mediaTrackType
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
    }
  ) {
    const localTracks = context.state.localTracks.filter(
      el =>
        el.channelName === channelName &&
        el.mediaTrackType == mediaTrackType &&
        (el.trackStatus == LocalTrackStatus.PUBLISHED ||
          el.trackStatus == LocalTrackStatus.UNPUBLISHED ||
          el.trackStatus == LocalTrackStatus.CREATED)
    );
    const client = context.state.mediaRooms[channelName].client;
    (localTracks || []).forEach(track => {
      track.track?.setMuted(true);
      client.unpublish([track.track as ILocalTrack]);
    });
  },
  async unpublishLocalTracks(
    context: MediaRoomContext,
    {
      channelName,
      mediaTrackType,
      forceDestroyTrack = false
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
      forceDestroyTrack: Boolean;
    }
  ) {
    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };
    // we can have multiple local tracks for audio tracks - so while unpublishing audio track
    // we need to pass and array of local tracks
    const localTracks = context.state.localTracks.filter(
      el =>
        el.channelName === channelName &&
        el.mediaTrackType == mediaTrackType &&
        (el.trackStatus == LocalTrackStatus.PUBLISHED ||
          el.trackStatus == LocalTrackStatus.UNPUBLISHED ||
          el.trackStatus == LocalTrackStatus.CREATED)
    );

    if (!localTracks.length) {
      // Nothing to unpublish
      const relatedTracksInProcessing = context.state.localTracks.filter(
        el => el.channelName === channelName && el.mediaTrackType == mediaTrackType
      );
      if (relatedTracksInProcessing.length) {
        setTimeout(() => {
          context.dispatch('unpublishLocalTracks', {
            channelName,
            mediaTrackType,
            forceDestroyTrack
          });
        }, 1000);
      }
      return;
    }

    const updateTrackStatusForChannel = (status: LocalTrackStatus) => {
      localTracks.forEach((t: LocalTrack) => {
        context.commit('updateLocalTrack', {
          channelName,
          mediaTrackType: t.mediaTrackType,
          updateObj: {
            trackStatus: status
          }
        });
      });
    };
    if (localTracks) {
      const mediaRoom = context.state.mediaRooms[channelName];

      try {
        const publishedTracks = localTracks.filter(track => track.trackStatus === LocalTrackStatus.PUBLISHED);
        if (publishedTracks.length) {
          updateTrackStatusForChannel(LocalTrackStatus.UNPUBLISHING);
          const trackObjects = <ILocalTrack[]>localTracks.map(t => t.track);
          await unpublishLocalTracks(mediaRoom.client, trackObjects);
          updateTrackStatusForChannel(LocalTrackStatus.UNPUBLISHED);
          context.dispatch('subscribeToTrackEnded', { localTracks, shouldUnsubscribe: true });
        }
        await context.dispatch('closeTrackAndReleaseResources', {
          channelName,
          mediaTrackType,
          forceDestroyTrack
        });
      } catch (error) {
        updateTrackStatusForChannel(LocalTrackStatus.PUBLISHED);
        NotificationService.toast(
          `${i18n.global.t('@@translate@@ Error unpublishing stream')}`,
          `${i18n.global.t('@@translate@@ Error unpublishing stream. Please refresh and try again!')}`,
          'error',
          null,
          AGORA.NOTIFICATION_TIMEOUT
        );
      }
    }
  },
  async exitRoom(context: MediaRoomContext, payload: { channelName: MediaRoom['channelName'] }) {
    const { channelName } = payload;
    if (!channelName) {
      return;
    }

    const room = context.state.mediaRooms[channelName];

    if (!room) {
      await Promise.all([
        context.dispatch('unpublishLocalTracks', {
          channelName: channelName,
          mediaTrackType: 'audio',
          forceDestroyTrack: true
        }),
        context.dispatch('unpublishLocalTracks', {
          channelName: channelName,
          mediaTrackType: 'video',
          forceDestroyTrack: true
        })
      ]);

      return;
    }

    context.commit('setMediaChannelDesiredStatus', {
      channelName,
      desiredStatus: 'DISCONNECTED'
    });

    await Promise.all([
      context.dispatch('unpublishLocalTracks', {
        channelName: channelName,
        mediaTrackType: 'audio',
        forceDestroyTrack: true
      }),
      context.dispatch('unpublishLocalTracks', {
        channelName: channelName,
        mediaTrackType: 'video',
        forceDestroyTrack: true
      }),
      context.dispatch('stopScreenSharing', {
        channelName: channelName
      })
    ]);

    closeJoiningDelayNetworkErrorModal();

    // Exiting room async and removing it from the state
    exitMediaRoom(room);
    toggleAudioLevelTracking('off');
    // Detach denoiser from the current track (keeps processor alive for re-pipe in next room)
    await detachNoiseCancellation();
    context.commit('removeMediaRoom', channelName);
  },
  async exitAllRoomsAsync(context: MediaRoomContext) {
    const channels = Object.keys(context.state.mediaRooms);
    // Wait for every exitRoom to finish (unpublish + detach) before destroying the processor —
    // otherwise destroy() races with the audio tracks still piped to it.
    await Promise.all(channels.map(channelName => context.dispatch('exitRoom', { channelName })));
    // Full teardown — user is leaving the event entirely
    await cleanupNoiseCancellation();
  },
  async initializeIndividualDevice(
    context: MediaRoomContext,
    deviceType: 'camera' | 'microphone' | 'speaker'
  ): Promise<MediaDeviceInfo[] | undefined> {
    try {
      let deviceList: MediaDeviceInfo[] = [];
      if (deviceType == 'camera') {
        deviceList = await getCamaraDevices(true);
      }
      if (deviceType == 'microphone') {
        deviceList = await getMicrophoneDevices(true);
      }
      if (deviceType == 'speaker') {
        if (window.mobileCheck && window.mobileCheck()) {
          console.warn('Disabled speaker device detection on mobile');
        } else {
          deviceList = await getSpeakerDevices(true);
        }
      }

      const santizedDeviceList = deviceList.filter(device => {
        return device?.label && device?.deviceId;
      });

      context.commit('setRtcDevices', {
        [deviceType + 's']: santizedDeviceList
      });

      let device: MediaDeviceInfo | null | undefined = context.state.currentDevices[deviceType];

      const oldCurrentDevice = device;
      if (santizedDeviceList.length > 0) {
        device =
          santizedDeviceList.find(d => d.deviceId === device?.deviceId) ||
          santizedDeviceList.find(d => d.deviceId === 'default') ||
          santizedDeviceList[0];
      }
      if (
        oldCurrentDevice?.deviceId !== device?.deviceId ||
        (device?.deviceId === 'default' && device?.label !== oldCurrentDevice?.label)
      ) {
        await context.dispatch('switchDevice', {
          deviceTag: deviceType,
          device
        });
      }

      return santizedDeviceList;
    } catch (error) {
      loggerService.error('Error while initializing cameras', error);
      if ((error as any).code == 'PERMISSION_DENIED') {
        context.commit('setRtcDeviceBrowserPermission', {
          deviceType: deviceType,
          permission: false
        });
      }
      context.commit('setRtcDeviceAccessError', {
        errorDevice: deviceType,
        errorFlag: true
      });
    }
  },
  async initializeDevices(context: MediaRoomContext): Promise<void> {
    await context.dispatch('initializeIndividualDevice', 'camera');
    await context.dispatch('initializeIndividualDevice', 'microphone');
    await context.dispatch('initializeIndividualDevice', 'speaker');
  },
  switchDeviceSuccess(context: MediaRoomContext, { deviceTag, device }: { deviceTag: string; device: any }) {
    if (deviceTag) {
      context.commit('setCurrentDevice', {
        device: device,
        deviceType: deviceTag
      });
      if (deviceTag === 'speaker') {
        context.dispatch('simuliveroom/setActiveSpeakerDevice', null, {
          root: true
        });
        context.dispatch('liveVideoRoom/setActiveSpeakerDevice', null, {
          root: true
        });
      }
    } else {
      console.warn('Device not recognized');
    }
  },
  async setDevice(
    context: MediaRoomContext,
    {
      deviceTag,
      device,
      localTrack
    }: {
      deviceTag: string;
      device: any;
      localTrack: ICameraVideoTrack | IMicrophoneAudioTrack;
    }
  ) {
    try {
      if (localTrack) {
        await setMediaDevice(device.deviceId, localTrack);
      }
      context.dispatch('switchDeviceSuccess', {
        deviceTag,
        device
      });
    } catch (error) {
      context.dispatch('switchDeviceError', {
        deviceTag,
        error: error
      });
    }
  },
  async switchDevice(context: MediaRoomContext, { deviceTag, device }: { deviceTag: string; device: any }) {
    if (deviceTag === 'camera') {
      const localVideoTrack = context.state.localTracks?.find(ele => ele.mediaTrackType == 'video');
      await context.dispatch('setDevice', {
        deviceTag,
        device,
        localTrack: localVideoTrack?.track
      });
      if (localVideoTrack?.localDuplicateTrack) {
        await setMediaDevice(device.deviceId, localVideoTrack.localDuplicateTrack);
      }
    }
    if (deviceTag === 'microphone') {
      const localAudioTracks = context.state.localTracks?.filter(ele => ele.mediaTrackType == 'audio');
      localAudioTracks.forEach(async ({ track }) => {
        await context.dispatch('setDevice', {
          deviceTag,
          device,
          localTrack: track
        });
      });
    }
    if (deviceTag === 'speaker') {
      context.dispatch('switchDeviceSuccess', { deviceTag, device });
    }
  },
  async createLocalDuplicateTrack(context: MediaRoomContext): Promise<ICameraVideoTrack> {
    let localDuplicateTrack;
    try {
      // Try enforcing browser to use 1080p 30fps
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: context.state.currentDevices.camera?.deviceId
            ? { exact: context.state.currentDevices.camera.deviceId }
            : undefined,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });
      const mediaStreamTrack = stream.getVideoTracks()[0];
      const AgoraRTC = await getAgoraRTC();
      localDuplicateTrack = AgoraRTC.createCustomVideoTrack({
        mediaStreamTrack
      });
    } catch (error) {
      loggerService.error('Error while creating local duplicate track', error);
      localDuplicateTrack = await createLocalVideoTrackWithCustomQuality({
        encoderConfig: '1080p_3',
        cameraId: context.state.currentDevices.camera?.deviceId
      });
    }

    loggerService.info(
      '[Local Recording] Created duplicate track',
      localDuplicateTrack.getTrackId(),
      localDuplicateTrack.getMediaStreamTrack(),
      localDuplicateTrack.getMediaStreamTrack().getSettings(),
      localDuplicateTrack.getMediaStreamTrack().getCapabilities()
    );
    const width = localDuplicateTrack.getMediaStreamTrack().getSettings().width;

    if (width) {
      try {
        const encoding = getEncodingBasedOnWidth(width);
        loggerService.info(
          `[Local Recording] setting encoding ${encoding} to duplicate agora track for width ${width}`
        );
        await localDuplicateTrack.setEncoderConfiguration(encoding);
      } catch (error) {
        loggerService.error(
          'Error while setting encoding to duplicate agora track',
          error,
          localDuplicateTrack.getMediaStreamTrack().getSettings(),
          localDuplicateTrack.getMediaStreamTrack().getCapabilities()
        );
      }
    }
    return localDuplicateTrack;
  },
  switchDeviceError(context: MediaRoomContext, { deviceTag, error }: { deviceTag: string; error: string }) {
    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };
    NotificationService.toast(
      `${i18n.global.t('@@translate@@ Failed to switch {deviceTag}', { deviceTag })}`,
      `${i18n.global.t(
        '@@translate@@ Make sure your {deviceTag} is not being used by any other application and is working properly.',
        { deviceTag }
      )}`,
      'error',
      null,
      15000
    );

    const trackType = deviceTag === 'camera' ? 'video' : 'audio';

    if (!context.state.localTracks.find((track: LocalTrack) => track.mediaTrackType === trackType)) {
      context.commit('setRtcDeviceAccessError', {
        errorDevice: deviceTag,
        errorFlag: true
      });
    }
    console.warn('failed to switch device', error);
  },
  setCurrentDeviceMedia(
    context: MediaRoomContext,
    {
      deviceType,
      rtcDeviceArr
    }: {
      deviceType: 'camera' | 'microphone' | 'speaker';
      rtcDeviceArr: 'cameras' | 'microphones' | 'speakers';
    }
  ) {
    const currentDeviceId = context.state.currentDevices[deviceType]?.deviceId;
    const device = context.state.rtcDevices[rtcDeviceArr].find(d => d.deviceId === currentDeviceId);
    if (!currentDeviceId || !device) {
      context.commit('setCurrentDevice', {
        device: context.state.rtcDevices[rtcDeviceArr][0],
        deviceType
      });
    } else {
      context.commit('setCurrentDevice', {
        device,
        deviceType
      });
    }
    context.dispatch('simuliveroom/setActiveSpeakerDevice', null, {
      root: true
    });
    context.dispatch('liveVideoRoom/setActiveSpeakerDevice', null, {
      root: true
    });
  },
  switchVideoQuality(context: MediaRoomContext, quality: Object) {
    const useFullHD = context.rootGetters['event/useFullHD'];
    let selectedQuality = quality;

    // Prevent selecting 1080p if Full HD is not enabled
    if ((quality as any)?.name === 'ULTRA_HIGH' && !useFullHD) {
      loggerService.warn('[VIDEO QUALITY] Attempted to select 1080p but Full HD not enabled');
      // Fallback to 720p
      selectedQuality = AGORA.VIDEO_QUALITY.find(vq => vq.name === 'HIGH') || AGORA.VIDEO_QUALITY[0];
    }

    (context.commit as any)('setSelectedVideoQuality', selectedQuality, { root: true });
    const track = context.state.localTracks.find(
      trackMeta => trackMeta.track && trackMeta.mediaTrackType === 'video'
    )?.track;

    if (track) {
      switchVideoQuality(track as ICameraVideoTrack, selectedQuality);
    }
  },
  async openSettingsDialog(
    context: MediaRoomContext,
    payload: {
      page?: 'broadcasts' | 'discussion room' | 'tv';
      setting?: 'audio' | 'video' | 'virtualBackground' | 'floatingReactions';
    }
  ) {
    const { page, setting } = payload;
    if (
      ['SpeakerTechSetup', 'AttendeeTechSetup', 'UngatedTechCheck', 'EventSplash'].includes(
        context.rootState.route.name?.toString() ?? ''
      )
    ) {
      return;
    }
    const dialogReferenceIndex: any = null;
    const options = {
      cancelOnClickOutside: false,
      role: 'dialog',
      on: {
        onImproveConnection: () => {
          window.location.reload();
          analytics.track(page || 'unknown', {
            type: 'click_improve_connection'
          });
        },
        onCancel: async () => {
          if (dialogReferenceIndex !== null) {
            DialogService.close(dialogReferenceIndex);
          }
        }
      }
    };

    const isAttendee = !context.rootGetters['event/isOrganizerOrSpeaker'];

    analytics.track('settings_modal_open', {
      eventId: context.rootState.route?.params?.eventID.toString(),
      broadcastId: context.rootState.route?.params?.id?.toString(),
      location: page,
      isAttendee
    });

    const onDemandMode = context.rootState.event.event?.on_demand_mode;
    const avInputEnabled = !(page === 'broadcasts' && onDemandMode);

    context.dispatch('initializeDevices').then(() => {
      const { index } = DialogService.show(
        AudioVideoSettingsModal,
        {
          id: 'settingsPopup',
          startWithTab: setting
        },
        options as any
      );
    });
  },
  async handleConnectionStateChange(
    context: MediaRoomContext,
    payload: {
      currentState: ConnectionState;
      channelName: MediaRoom['channelName'];
      connectionDisconnectedReason?: ConnectionDisconnectedReason;
    }
  ) {
    const { currentState, channelName, connectionDisconnectedReason } = payload;
    if (context.state.mediaRooms[channelName]) {
      context.commit('setMediaChannelStatus', {
        channelName,
        status: currentState,
        connectionDisconnectedReason
      });
    }
  },

  async handleConnectionStateChangeForScreen(
    context: MediaRoomContext,
    payload: {
      currentState: ConnectionState;
      channelName: MediaRoom['channelName'];
      connectionDisconnectedReason?: ConnectionDisconnectedReason;
    }
  ) {
    const { currentState, channelName, connectionDisconnectedReason } = payload;
    context.commit('setMediaChannelScreenStatus', {
      channelName,
      status: currentState,
      connectionDisconnectedReason
    });
  },
  async addRemoteUser(
    context: MediaRoomContext,
    payload: {
      user: IAgoraRTCRemoteUser;
      channelName: MediaRoom['channelName'];
    }
  ) {
    const { channelName, user } = payload;
    context.commit('addOrUpdateUserTrack', {
      channelName,
      user
    });
    setTimeout(() => {
      context.commit('capacityVerified', {
        channelName,
        user
      });
    }, AGORA.CAPACITY_CHECK_DELAY);

    context.dispatch('triggerLayoutUpdate', user);

    // TODO: Refactor this
    const addUserDataToRemoteStream = () => {
      if ((<string>user.uid).includes(AGORA.UID_SUFFIX.LIVE_STREAMER)) {
        let userName = '@@translate@@ Live Feed';
        if (context.rootState.event.event?.theme_obj?.hide_live_feed_name_card) {
          userName = '';
        }
        return context.commit('addOrUpdateUserData', {
          channelName,
          userId: user.uid,
          userData: {
            name: userName
          }
        });
      }
      const goldcastUid = (<string>user.uid).split('|')[0];
      let userData = context.rootState.event.event?.users[goldcastUid];
      if (!userData) {
        userData = {};
        loggerService.warn('User not found for stream, scheduling fetch: ', goldcastUid);
        scheduleUserFetch(<string>goldcastUid);
        setTimeout(() => {
          addUserDataToRemoteStream();
        }, 5000);
      } else {
        context.commit('addOrUpdateUserData', {
          channelName,
          userId: user.uid,
          userData
        });
      }
    };

    if (!(<string>user.uid).includes(AGORA.UID_SUFFIX.SCREEN_STREAM)) {
      addUserDataToRemoteStream();
    }
    context.dispatch('updateActivityInRoom');
  },
  async removeRemoteUser(
    context: MediaRoomContext,
    payload: {
      user: IAgoraRTCRemoteUser;
      channelName: MediaRoom['channelName'];
    }
  ) {
    const { channelName, user } = payload;
    context.commit('removeRemoteUser', {
      channelName,
      userId: user.uid
    });
    context.dispatch('updateActivityInRoom');
  },
  async addRemoteUserTrack(
    context: MediaRoomContext,
    payload: {
      user: IAgoraRTCRemoteUser;
      channelName: MediaRoom['channelName'];
      mediaType: ITrack['trackMediaType'];
    }
  ) {
    const { user, channelName, mediaType } = payload;
    context.commit('addOrUpdateUserTrack', {
      mediaType,
      channelName,
      user
    });
  },
  async removeRemoteUserTrack(
    context: MediaRoomContext,
    payload: {
      user: IAgoraRTCRemoteUser;
      channelName: MediaRoom['channelName'];
      mediaType: ITrack['trackMediaType'];
    }
  ) {
    const { mediaType, channelName, user } = payload;
    context.commit('addOrUpdateUserTrack', {
      mediaType,
      channelName,
      user
    });
  },
  async checkAndUpdateLocalNetworkQuality(
    context: MediaRoomContext,
    payload: {
      networkQuality: NetworkQuality;
    }
  ) {
    const networkQualityPoints = getNetworkQualityDatapoints();
    const uplinkPoints = networkQualityPoints.uplink.slice(-5);
    const downlinkPoints = networkQualityPoints.downlink.slice(-5);
    const uplinkAggregate = getAggregate(uplinkPoints, payload.networkQuality.uplinkNetworkQuality);
    const downlinkAggregate = getAggregate(downlinkPoints, payload.networkQuality.downlinkNetworkQuality);

    const meanUplink = uplinkAggregate?.average;
    const meanDownlink = downlinkAggregate?.average;

    const isUplinkSame = meanUplink === context.state.signalMetadata.networkQuality.uplinkNetworkQuality;
    const isDownlinkSame = meanDownlink === context.state.signalMetadata.networkQuality.downlinkNetworkQuality;

    if (isUplinkSame && isDownlinkSame) {
      return;
    }

    context.commit('setSignalMetadata', {
      networkQuality: {
        uplinkNetworkQuality: Math.round(meanUplink || 0),
        downlinkNetworkQuality: Math.round(meanDownlink || 0)
      }
    });
  },
  async stopScreenSharing(context: MediaRoomContext, payload: { channelName: string }) {
    if (context.state.screenTracks?.length) {
      context.dispatch('updateRecordingScreenShareLayout', null);
    }

    context.state.mediaRooms[payload.channelName]?.screenShareClient?.leave();
    closeTracks(<ILocalTrack[]>context.state.screenTracks.map(t => t?.track));
    context.commit('removeScreenTracks');
    context.dispatch('sidebar/stopLocalSlideRecording', {}, { root: true });
  },
  handleNetworkError(
    context: MediaRoomContext,
    {
      type = 'GENERIC_NETWORK_ERROR',
      title = 'We detected a network issue',
      bodyText = ''
    }: {
      type?: string;
      title?: string;
      bodyText?: string;
    } = {}
  ) {
    loggerService.warn('Detected Network Error!', { type });
    analytics.track('network_error', { type, title, bodyText });
    if (context.state.networkErrorIndex) {
      return;
    }

    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };

    let liteMode = false;
    const isCurrentPageBroadcast = context.rootState.route?.fullPath?.includes('/broadcast');
    const currentBroadcast = (context.rootState.event.event.agenda?.broadcasts ?? []).find(
      (b: any) => b.id === context.rootState.route.params.id
    );
    const isSpeakerOfCurrentBroadcast =
      currentBroadcast?.speakerIds?.includes(context.rootState.user.currentUser?.id) ?? false;
    const isEventOrganizer = context.rootGetters['isEventOrganizer'];
    const isAttendeeOnStage = !!getVideoQNARequestForCurrentUserByStatus(currentBroadcast, 'ACCEPTED');

    const viewAsAttendee = context.rootGetters['viewAsAttendee'];

    const isUserAlreadyInLiteMode = context.rootState.user.liteMode?.liteModeActive;

    if (
      isCurrentPageBroadcast &&
      !(isSpeakerOfCurrentBroadcast || isEventOrganizer || isAttendeeOnStage || viewAsAttendee) &&
      currentBroadcast.is_recording &&
      !isUserAlreadyInLiteMode
    ) {
      liteMode = true;
    }
    let text = bodyText;
    if (!text) {
      const ERRORS = i18n.global.t(`${NETWORK_STRINGS.CONNECTION_ERRORS.AGORA}`);
      const didEstablishConnection = context.state.didEstablishConnection;
      if (liteMode) {
        // can fallback to lite mode
        text = text = didEstablishConnection ? ERRORS.NETWORK_HAS_FALLBACK : ERRORS.VPN_HAS_FALLBACK;
      } else {
        // no fallback
        text = didEstablishConnection ? ERRORS.NETWORK_NO_FALLBACK : ERRORS.VPN_NO_FALLBACK;
      }
    }
    const isLiveStreamRoute = appMode === 'liveStream';
    const showButton = ['Mac OS', 'Windows'].includes(getOS()?.os || '');
    const buttonText = showButton ? i18n.global.t('@@translate@@ Check Your Network') : '';
    const bannerText = showButton ? '' : i18n.global.t('@@translate@@ Check Your Network');

    const options: any = {
      placement: isLiveStreamRoute ? 'center-center' : 'stage-center',
      hideOverlay: isLiveStreamRoute ? false : true,
      cancelOnClickOutside: false,
      blockBackgroundActions: isLiveStreamRoute ? true : false,
      closeOnCTAClick: false,
      on: {
        onButtonAction: async () => {
          loggerService.info('Button Action Clicked on Network Error Dialog', {
            type
          });

          if (getOS()?.os === 'Mac OS') {
            window.open('x-apple.systempreferences:com.apple.preference.network', '_blank');
          }
          if (getOS()?.os === 'Windows') {
            window.open('ms-settings:network', '_blank');
          }
        },
        onTurnOnLiteMode: async () => {
          loggerService.info('Switching to Lite Mode', { type });
          context
            .dispatch('toggleToLiteMode', null, {
              root: true
            })
            .then(() => {
              context.dispatch('exitAllRoomsAsync');
              closeJoiningDelayNetworkErrorModal();
            })
            .catch(error => {
              loggerService.error('Error toggling to lite mode!', {
                type,
                error
              });
            });
        }
      }
    };

    const { promise, index } = DialogService.show(
      NetworkErrorDialog,
      {
        title,
        liteMode,
        text,
        buttonText,
        bannerText
      },
      options as any
    );

    context.commit('setNetworkErrorIndex', index);

    analytics.track('network_error_dialog_shown', {
      title,
      liteMode,
      bodyText,
      buttonText,
      bannerText
    });
  },
  async setVirtualBackground(context: MediaRoomContext, value: string) {
    const videoTrack = context.state.localTracks.find(
      trackMeta => trackMeta.track && trackMeta.mediaTrackType === 'video'
    );
    if (videoTrack?.track) {
      if (value === 'blur') {
        await setBackgroundBlurring(videoTrack?.track as ICameraVideoTrack);
      } else if (value) {
        await setBackgroundImage(videoTrack?.track as ICameraVideoTrack, value);
      } else {
        await unsetBackgroundFilter(videoTrack?.track as ICameraVideoTrack);
      }
    }

    // We don't need to handle no effects since in that case, we will use pure local track, not duplicate track
    if (videoTrack?.localDuplicateTrack && !videoTrack?.isDuplicateTrackLive) {
      if (value === 'blur') {
        await setBackgroundBlurring(videoTrack?.localDuplicateTrack as ICameraVideoTrack);
      } else if (value) {
        await setBackgroundImage(videoTrack?.localDuplicateTrack as ICameraVideoTrack, value);
      } else {
        await unsetBackgroundFilter(videoTrack?.localDuplicateTrack as ICameraVideoTrack);
      }
    }

    if (value === context.state.currentBackgroundFilter) {
      return;
    }

    context.commit('setCurrentBackgroundFilter', value);

    const type = !value ? 'none' : value === 'blur' ? value : 'image';
    const hasImageFilters = context.getters['virtualBackgrounds'].length;
    if (hasImageFilters && type !== 'image') {
      context.commit('setUserBackgroundFilterPrefrence', { image: false });
    }
    if (type === 'none') {
      context.commit('setUserBackgroundFilterPrefrence', { blur: false });
    } else if (type === 'blur' || type === 'image') {
      context.commit('setUserBackgroundFilterPrefrence', { [type]: true });
    }

    const preferences = {
      ...context.state.userBackgroundFilterPreference,
      lastActiveBackgroundFilter: value ?? ''
    };
    saveValueToLocalstore(LOCAL_STORAGE_KEYS.userVirtualBgSpec, preferences);
  },
  async checkUserPreferencesAndSetFilter(context: MediaRoomContext) {
    const imageBackgrounds = context.getters['virtualBackgrounds'];
    const hasImageFilters = imageBackgrounds.length;
    const preferences = context.state.userBackgroundFilterPreference;
    const currentFilter = context.state.currentBackgroundFilter;
    const lastActiveFilter = getValueFromLocalstore(LOCAL_STORAGE_KEYS.userVirtualBgSpec, '');

    let indexOfLastActiveFilter = (imageBackgrounds as any).findIndex(
      item => item === lastActiveFilter.lastActiveBackgroundFilter
    );
    const isRecording = context.rootState.event.event.event_type === 'Recording';

    if (indexOfLastActiveFilter === -1 && !isRecording) {
      indexOfLastActiveFilter = 0;
    }

    if (hasImageFilters && preferences.image) {
      if (['', 'none', 'blur'].includes(currentFilter) && indexOfLastActiveFilter >= 0) {
        context.dispatch('setVirtualBackground', (imageBackgrounds as any)[indexOfLastActiveFilter]);
      } else {
        context.dispatch('setVirtualBackground', currentFilter);
      }
    } else if (preferences.blur) {
      context.dispatch('setVirtualBackground', 'blur');
    } else {
      context.dispatch('setVirtualBackground', '');
    }
  },
  async initNoiseCancellation(context: MediaRoomContext, track: IMicrophoneAudioTrack) {
    const noiseCancellationCompatible = context.state.noiseCancellationCompatible;
    if (noiseCancellationCompatible) {
      await initializeNoiseCancellation(track);
    } else {
      context.dispatch('toggleNoiseCancellation', false);
      loggerService.warn('Noise Cancellation not supported on this device');
    }
  },
  async subscribeToTrackEnded(
    context: MediaRoomContext,
    { localTracks, shouldUnsubscribe }: { localTracks: LocalTrack[]; shouldUnsubscribe: boolean }
  ) {
    const isRecording = context.rootState.event.event.event_type === 'Recording';
    if (!isRecording) {
      return;
    }
    const isLocalRecording = context.rootState.event.event.is_local_recording;

    const resetEndedTrack = async (track: LocalTrack) => {
      await context.dispatch('unpublishLocalTracks', {
        channelName: track.channelName,
        mediaTrackType: 'video'
      });
      setTimeout(() => {
        context.dispatch('createLocalTrack', {
          channelName: track.channelName,
          mediaTrackType: 'video',
          shouldMute: isLocalRecording ? !store.state.mediaroom.stageControls.video : undefined
        });
      }, 2000);
    };

    const unsubscribeFromTrackEnded = (track: LocalTrack) => {
      (track.track as ICameraVideoTrack).off('track-ended', resetEndedTrack);
    };
    localTracks.forEach(track => {
      if (track.mediaTrackType === 'video' && track.track) {
        if (shouldUnsubscribe) {
          unsubscribeFromTrackEnded(track);
        } else {
          (track.track as ICameraVideoTrack).on('track-ended', resetEndedTrack.bind(null, track));
        }
      }
    });
  },
  /**
   * Enables the noise cancellation (denoiser) and waits for the track to be updated.
   * If the session is not in a "Recording" state, it immediately enables noise cancellation
   * and returns the original track. If the session is "Recording", the function listens for
   * a `track-updated` event and resolves with the updated track once the event occurs since
   * 'track-updated' could cause local recording to stop and because adding noise cancellation
   * actually creates a new track with noise cancellation applied.
   * If the update takes longer than 10 seconds, the function resolves with the original track.
   *
   * @param {MediaRoomContext} context - The context of the media room
   * @param {IMicrophoneAudioTrack} track - The audio track to which noise cancellation will be applied.
   *
   * @returns {Promise<IMicrophoneAudioTrack>} - A promise that resolves with either the original track
   *                                              or the updated track after noise cancellation is applied.
   *
   * @throws {Error} - If there is an error during the noise cancellation initialization,
   *                   the promise is rejected with the error.
   *
   */
  async enableNoiseCancellationAndWaitForUpdate(context: MediaRoomContext, track: IMicrophoneAudioTrack) {
    const isRecording = context.rootState.event.event.is_local_recording;

    if (!isRecording) {
      return context.dispatch('initNoiseCancellation', track);
    }
    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        resolve(track);
      }, 10000);

      const handleTrackUpdated = newTrack => {
        clearTimeout(timeout);
        track.off('track-updated', handleTrackUpdated);
        resolve(newTrack);
      };

      track.on('track-updated', handleTrackUpdated);
      context.dispatch('initNoiseCancellation', track).catch(() => {
        clearTimeout(timeout);
        track.off('track-updated', handleTrackUpdated);
        resolve(track);
      });
    });
  },
  /**
   * Enables the video filter and waits for the track to be updated.
   * If the session is not in a "Recording" state, it immediately enables video filter
   * and returns the original track. If the session is "Recording", the function listens for
   * a `track-updated` event and resolves with the updated track once the event occurs since
   * 'track-updated' could cause local recording to stop and because adding video filter
   * actually creates a new track with video filter applied.
   * If the update takes longer than 10 seconds, the function resolves with the original track.
   *
   * @param {MediaRoomContext} context - The context of the media room
   * @param {ICameraVideoTrack} track - The video track to which video filter will be applied.
   *
   * @returns {Promise<ICameraVideoTrack>} - A promise that resolves with either the original track
   *                                              or the updated track after video filter is applied.
   *
   * @throws {Error} - If there is an error during the video filter initialization,
   *                   the promise is rejected with the error.
   *
   */
  async enableVideoFilterAndWaitForUpdate(
    context: MediaRoomContext,
    { track, hasFilters }: { track: ICameraVideoTrack; hasFilters: boolean }
  ) {
    const isRecording = context.rootState.event.event.is_local_recording;

    if (!isRecording || !hasFilters) {
      return context.dispatch('checkUserPreferencesAndSetFilter');
    }
    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        resolve(track);
      }, 10000);

      const handleTrackUpdated = newTrack => {
        clearTimeout(timeout);
        track.off('track-updated', handleTrackUpdated);
        resolve(newTrack);
      };

      track.on('track-updated', handleTrackUpdated);
      context.dispatch('checkUserPreferencesAndSetFilter', track).catch(() => {
        clearTimeout(timeout);
        track.off('track-updated', handleTrackUpdated);
        resolve(track);
      });
    });
  },
  async toggleNoiseCancellation(context: MediaRoomContext, value: boolean) {
    saveValueToLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationEnabled, value);
    context.commit('setNoiseCancellation', value);
    await toggleNoiseCancellation(value);
  },
  changeNoiseCancellationLevel(context: MediaRoomContext, value: AIDenoiserProcessorLevel) {
    saveValueToLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationLevel, value);
    setNoiseCancellationLevel(value);
    context.commit('setNoiseCancellationLevel', value);
  },
  updateActivityInRoom(context: MediaRoomContext) {
    // Only to log activities for the recorder
    if (!context.rootState.route.query.record_mode) {
      return;
    }
    try {
      const currentUtcTimestamp = context.rootState.event.stage?.heartBeatDate?.getTime() || new Date().getTime();
      const isBroadcast = context.rootState.route?.fullPath?.includes('/broadcast');

      if (!isBroadcast) {
        return;
      }

      const currentRoomId = context.rootState.route.params.id?.toString();
      const mediaRoomData = context.state.mediaRooms[currentRoomId] || {};
      const broadcastDetails = context.rootGetters['event/currentEventBroadcast'](currentRoomId);
      const broadcast_type = broadcastDetails?.broadcast_type;

      const isSimulive = broadcast_type === 'SIMULIVE';
      const isRtmpIn = broadcast_type === 'LIVE_EXTERNAL';
      const isLiveHybrid = broadcast_type === 'LIVE_HYBRID';

      const isSimuliveOrRtmpPlayingVideo = isSimulive || isRtmpIn;

      const isHybridSimulivePlayingVideo = broadcastDetails?.is_hybrid_playing_simulive;

      const isLiveHybridPlayingVideo =
        isLiveHybrid && broadcastDetails?.current_playing_video_asset && broadcastDetails?.playback_start_time;

      const is_video_being_played = !!(
        isHybridSimulivePlayingVideo ||
        isSimuliveOrRtmpPlayingVideo ||
        isLiveHybridPlayingVideo
      );

      const remoteStreams = Object.keys(mediaRoomData?.remoteStreams || {});
      const userStreamCount = remoteStreams.filter(id => !id.includes(AGORA.UID_SUFFIX.SCREEN_STREAM)).length;
      const screenCount =
        remoteStreams.filter(id => id.includes(AGORA.UID_SUFFIX.SCREEN_STREAM)).length ||
        context.rootGetters['sidebar/publishedStage'].visibleOnStage
          ? 1
          : 0;

      const isStageAccessible = isStageAccessibleToAttendee({
        broadcastDetails,
        event: context.rootState.event.event,
        currentTime: store.state.event.stage?.heartBeatDate || new Date(),
        useRecordingPlayPause: context.rootState.event.useRecordingPlayPause
      });
      const shouldRecord = isStageAccessible && (userStreamCount > 0 || screenCount > 0 || is_video_being_played);
      const recorderMetadata = JSON.stringify({
        users_in_room: userStreamCount,
        is_screen_shared: screenCount > 0,
        is_video_being_played: is_video_being_played,
        timestamp: currentUtcTimestamp,
        is_stage_accessible_to_user: isStageAccessible,
        should_record: shouldRecord
      });
      console.warn('ROOM_ACTIVITY:', recorderMetadata);
    } catch (err) {
      console.error('Error updating activity in room', err);
    }
  },
  async setRemoteVideoSubscription(
    context: MediaRoomContext,
    payload: { channelName: MediaRoom['channelName']; value: boolean }
  ) {
    const { channelName } = payload;
    const mediaRoomClient = context.state.mediaRooms[channelName].client;
    if (mediaRoomClient) {
      const remoteUsers = mediaRoomClient.remoteUsers.map((user: IAgoraRTCRemoteUser) => ({
        user: user,
        mediaType: 'video'
      }));
      if (!payload.value) {
        // @ts-ignore
        mediaRoomClient.massUnsubscribe(remoteUsers);
        remoteUsers.forEach(({ user, mediaType }) => {
          context.dispatch('removeRemoteUserTrack', {
            user: { ...user, videoTrack: undefined },
            mediaType,
            channelName
          });
        });
      } else {
        // @ts-ignore
        await mediaRoomClient.massSubscribe(remoteUsers);
        remoteUsers.forEach(({ user, mediaType }) => {
          context.dispatch('addRemoteUserTrack', {
            user,
            mediaType,
            channelName
          });
        });
      }
    }
  },
  async enableAgoraLogs(context: MediaRoomContext) {
    enableAgoraLogs();
  }
};

export default actions;
